import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, ScanLine, FileSpreadsheet, Users, ShieldCheck, Globe } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';

const FAQS = [
  { q: 'What is small business bookkeeping?', a: 'Small business bookkeeping is keeping an organized, tax-ready record of your company’s income, expenses, receipts and reports. MoneyFeed lets your team capture expenses on WhatsApp, bulk-scans receipts, and exports accountant-ready CSV/Excel — without the cost of full accounting software.' },
  { q: 'How should a small business track expenses?', a: 'Capture each expense as it happens and keep the receipt. With MoneyFeed, up to five team members text expenses on WhatsApp or snap receipts; each is categorized to the right tax line and archived, so your books stay current and audit-ready.' },
  { q: 'Can multiple team members submit expenses?', a: 'Yes. Up to 5 people (Manager, Accountant, Staff roles) can text expenses into one company account, tagged with who submitted them.' },
  { q: 'Can I hand the data to my accountant?', a: 'Yes — export clean, categorized CSV/Excel/PDF reports with receipt images attached, or invite your accountant directly. See MoneyFeed for accountants.' },
];

const FEATURES = [
  { icon: ScanLine, title: 'Bulk invoice & receipt scanning', desc: 'Drag & drop a whole folder of receipts. Our AI reads the amount, merchant, date and tax on each — then you review and import in one click.' },
  { icon: FileSpreadsheet, title: 'CSV & Excel export center', desc: 'Export clean, accountant-ready transaction spreadsheets filtered by date range, type or category — hand straight to your bookkeeper.' },
  { icon: Users, title: 'Team roles & expense capture', desc: 'Up to 5 people (Manager, Accountant, Staff) text expenses on WhatsApp — they roll into one company account, tagged with who submitted them.' },
  { icon: Globe, title: 'Multi-country tax-ready', desc: 'Schedule C, Self Assessment, T2125 and VAT — MoneyFeed maps every expense to the right tax line for the US, UK, Canada, UAE & Saudi Arabia.' },
  { icon: ShieldCheck, title: 'CPA audit trail', desc: 'Every scanned receipt is archived and linked to its transaction, so you always have proof for the tax authority.' },
  { icon: Building2, title: 'Built for small businesses', desc: 'All the tax-ready books an enterprise needs, without the complexity — or cost — of full accounting software.' },
];

export default function Business() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="business-page">
      <Seo
        title="MoneyFeed for Business — Bulk Receipt Scanning & Excel Export"
        description="Tax-ready bookkeeping for small businesses & accountants. Bulk-scan invoices, capture team expenses on WhatsApp, and export accountant-ready CSV/Excel reports."
        path="/business"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#064E3B]/10 rounded-full mb-5">
          <Building2 className="h-4 w-4 text-[#064E3B]" />
          <span className="text-xs font-bold text-[#064E3B] uppercase tracking-wider">MoneyFeed for Business</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Tax-ready books for your <span className="text-[#84CC16]">whole business</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Bulk-scan invoices, capture team expenses on WhatsApp, and export accountant-ready CSV & Excel reports — all without the cost and complexity of traditional accounting software.
        </p>
        <div className="bg-white rounded-2xl shadow-md p-6 text-left max-w-2xl mx-auto mb-2" data-testid="business-answer">
          <p className="text-sm sm:text-base text-gray-700">
            <strong>Small business bookkeeping</strong> means keeping an organized, tax-ready record of your company’s income, expenses, receipts and reports. <strong>MoneyFeed</strong> lets your team capture expenses on WhatsApp, bulk-scans receipts, and exports accountant-ready CSV/Excel — without the cost of full accounting software.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="business-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <Link to="/invoice-scanner">
            <Button variant="outline" className="w-full sm:w-auto border-[#064E3B]/30 text-[#064E3B] hover:bg-[#064E3B]/5 font-bold rounded-full px-7 py-6 text-base" data-testid="business-scanner-link">
              See the invoice scanner
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`business-feature-${f.title}`}>
              <div className="w-11 h-11 rounded-xl bg-[#F0FDF4] flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#064E3B]" />
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{f.title}</h3>
              <p className="text-gray-600 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Ready to close your books faster?</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start scanning receipts and exporting accountant-ready reports today. Free to try, upgrade when your team grows.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="business-cta-signup">
                Get started free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/for-accountants">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="business-accountants-link">
                For accountants & bookkeepers
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Small business bookkeeping FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`business-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/invoice-scanner" className="hover:text-[#064E3B]">Invoice Scanner</Link>
          <Link to="/export-to-excel" className="hover:text-[#064E3B]">Export to Excel</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
        </div>
      </footer>
    </div>
  );
}
